import type { ThanksConfig } from '@coorum-research/shared-common/types'
import type { SurveyAnswerRecord } from '@coorum-research/shared-common/types/answer.type'
import type { SurveyConfig } from '@coorum-research/shared-common/types/survey-json.type'
import type { ThemeConfig } from '@coorum-research/shared-common/types/theme.type'
import type { Question } from 'survey-core'
import { isString } from 'es-toolkit'
import { useMemo } from 'react'
import { Survey, SvgBundleComponent } from 'survey-react-ui'
import { useSurveyModel } from '../composables/use-survey-model'
import { Card } from './Card'
import { CdsButton } from './cds/CdsButton'
import { SurveyAnswerListItem } from './SurveyAnswerListItem'
import { ThanksComponent } from './ThanksComponent'

type Props = {
  surveyName: string | null
  createdAt: string
  surveyConfig: SurveyConfig | string | null
  surveyThemeConfig?: ThemeConfig | null
  surveyAnswer: string | null
  thanksContentName?: string | null
  thanksContentConfig?: ThanksConfig | null
  // 回答の修正のためのアンケート開始
  onEditStart?: () => void
}

export function SurveyAnswerList({
  surveyName,
  createdAt,
  surveyConfig,
  surveyThemeConfig,
  surveyAnswer,
  thanksContentName,
  thanksContentConfig,
  onEditStart,
}: Props) {
  /**
   * 未回答の質問を除外したアンケート設定
   * TODO: SurveyWithConfigResponseへの切り替え完了後削除
   * apiを切り替えてなくSurveyResponseを使ってる部分のため、surveyConfigで、stringも受け入れる。(SurveyWithConfigResponseを推奨)
   */
  const surveyConfigJson = useMemo(() => {
    if (!surveyConfig || !surveyAnswer) {
      return null
    }

    try {
      const surveyConfigJson = isString(surveyConfig) ? JSON.parse(surveyConfig) as SurveyConfig : surveyConfig

      /**
       * 回答されている質問IDsにフィルターする
       */
      const surveyAnswerJson = JSON.parse(surveyAnswer) as SurveyAnswerRecord
      const answeredQuestionIds = Object.keys(surveyAnswerJson)

      if (surveyConfigJson.elements) {
        surveyConfigJson.elements = surveyConfigJson.elements.filter(ele => answeredQuestionIds.includes(ele.name))
      }

      return surveyConfigJson
    } catch (e) {
      console.error(e)
      return null
    }
  }, [surveyConfig, surveyAnswer])

  const { model } = useSurveyModel({
    surveyConfig: surveyConfigJson,
    themeJson: surveyThemeConfig,
    resultData: surveyAnswer,
    mode: 'display',
  })

  // panel質問を含む全ての質問を取得
  // memo: model.getAllQuestions()だとpanelの質問が取得できないため、手動で取得
  const questions = useMemo(() => {
    const allQuestions: Question[] = []
    model.pages.forEach((page) => {
      page.elements.forEach((element) => {
        allQuestions.push(element as Question)
      })
    })
    return allQuestions
  }, [model])

  const creator = useMemo(() => {
    return new Survey({ model })
  }, [model])

  // 回答データをパース
  const parsedAnswerData = useMemo(() => {
    if (!surveyAnswer) {
      return {}
    }

    try {
      return JSON.parse(surveyAnswer) as SurveyAnswerRecord
    } catch (e) {
      console.error('Failed to parse survey answer:', e)
      return {}
    }
  }, [surveyAnswer])

  return (
    <>
      <SvgBundleComponent />
      <div className="grid h-full grid-rows-[auto_1fr] gap-4">
        <div className="flex flex-col gap-2 px-6">
          <h1 className="line-clamp-2 shrink-0 text-2xl font-bold">
            {surveyName}
          </h1>
          <div className="mt-1 flex items-center justify-between">
            <div className="text-lg">
              {new Date(createdAt).toLocaleDateString()}
            </div>
            {onEditStart && (
              <div>
                <CdsButton
                  variant="normal"
                  size="md"
                  onClick={() => onEditStart()}
                >
                  回答の編集
                </CdsButton>
              </div>
            )}
          </div>
        </div>
        <div
          className="flex flex-col gap-3 overflow-y-auto px-6 pb-4"
          style={model.themeVariables}
        >
          {
            questions.length > 0
              ? (
                <>
                  {questions.map(question => (
                    question.isVisible && (
                      <Card key={question.name} className="flex flex-col gap-2 p-4">
                        <div className="text-base font-bold">
                          {question.title}
                        </div>
                        <hr className="bg-[#E2E3E2]" />
                        <div className="text-sm">
                          <SurveyAnswerListItem
                            creator={creator}
                            question={question}
                            answerData={parsedAnswerData}
                          />
                        </div>
                      </Card>
                    )
                  ))}
                </>
              )
              : (
                <p>回答が読み込めませんでした</p>
              )
          }
          {(!!thanksContentName || !!thanksContentConfig) && (
            <Card className="flex flex-col gap-2 p-4">
              <div className="text-base font-bold">
                回答結果
              </div>
              <hr className="bg-[#E2E3E2]" />
              {thanksContentConfig
                ? (
                  <ThanksComponent content={thanksContentConfig} />
                )
                : (
                  <div className="text-sm">
                    {thanksContentName}
                  </div>
                )}
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
